import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useEventWebSocket } from '../../../../hooks/useEventWebSocket'
import { EventAdminService } from '../../../../services/Event/EventAdminService'
import { LiveArtBid, LiveArtwork } from '../../../../services/Event/types'

/** Enquanto o WebSocket estiver fora do ar, a tela se mantém atualizada por polling. */
const POLLING_INTERVAL_MS = 5000

/** Tempo que cada obra fica em destaque antes de o carrossel avançar. */
export const ROTATION_INTERVAL_MS = 7000

/** Mesmo limite que a API aplica em `recent_bids`, para a lista não crescer sem fim na tela. */
const ART_BIDS_LIMIT = 15

interface IUseAuctionScreenOptions {
  eventId: number | null
}

interface IUseAuctionScreenResult {
  artworks: LiveArtwork[]
  featured: LiveArtwork | null
  featuredIndex: number
  peopleCount: number
  auctionEndsAt: string | null
  auctionEnded: boolean
  /** Encerrado E com as obras já decididas pelo worker (`sold`/`unsold`). */
  auctionFinalized: boolean
  isConnected: boolean
  isLoading: boolean
  loadError: string | null
  goToIndex: (index: number) => void
  goToNext: () => void
  goToPrevious: () => void
}

/** A tela do telão só exibe obras aprovadas e liberadas para leilão, na ordem de cadastro. */
const toScreenArtworks = (artworks: LiveArtwork[]): LiveArtwork[] =>
  artworks
    .filter((artwork) => artwork.available_for_auction && artwork.exhibition_status === 'approved')
    .sort((a, b) => a.id - b.id)

export function useAuctionScreen({ eventId }: IUseAuctionScreenOptions): IUseAuctionScreenResult {
  const [allArtworks, setAllArtworks] = useState<LiveArtwork[]>([])
  const [featuredIndex, setFeaturedIndex] = useState(0)
  const [peopleCount, setPeopleCount] = useState(0)
  const [auctionEndsAt, setAuctionEndsAt] = useState<string | null>(null)
  const [auctionEnded, setAuctionEnded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Ids dos lances que a tela inventa a partir do WebSocket. Decrescem a partir de -1:
  // os ids reais vindos do snapshot são positivos, então nunca colidem — e um contador não
  // repete, ao contrário de `Date.now()` com dois lances no mesmo milissegundo.
  const localBidIdRef = useRef(0)

  // O índice em destaque é volátil (o carrossel gira sozinho), mas o refetch precisa
  // manter a MESMA obra em destaque — por isso guardamos o id, não a posição.
  const featuredArtIdRef = useRef<number | null>(null)

  const loadSnapshot = useCallback(async () => {
    if (!eventId) return

    try {
      const data = await new EventAdminService().getLiveData(eventId, {
        includeNonAuction: false,
        includeArtBids: true
      })

      const screenArtworks = toScreenArtworks(data.artworks)

      setAllArtworks(screenArtworks)
      setPeopleCount(data.stats.active_checkins)
      setAuctionEndsAt(data.auction_ends_at)
      setAuctionEnded(data.auction_ended)
      setLoadError(null)

      setFeaturedIndex((current) => {
        const previousId = featuredArtIdRef.current
        const sameArtIndex = previousId ? screenArtworks.findIndex((artwork) => artwork.id === previousId) : -1

        if (sameArtIndex >= 0) return sameArtIndex

        return screenArtworks.length ? Math.min(current, screenArtworks.length - 1) : 0
      })
    } catch {
      setLoadError('Não foi possível carregar os dados do leilão.')
    } finally {
      setIsLoading(false)
    }
  }, [eventId])

  const handleWsEvent = useCallback(
    (wsEvent: { event: string; data: Record<string, unknown> }) => {
      const { event: eventType, data } = wsEvent

      if (eventType === 'checkin') {
        setPeopleCount((count) => count + 1)
      }

      if (eventType === 'checkout') {
        setPeopleCount((count) => Math.max(0, count - 1))
      }

      if (eventType === 'bid') {
        const artId = data.art_id as number
        const bidAmount = String(data.bid_amount)
        const tabCode = (data.tab_code as string) || null
        const participantName = (data.participant_name as string) || null

        // O broadcast de lance não traz id nem created_at (ver EVENT_TERMINAL_API.md), então
        // sintetizamos os dois — só servem para a key da lista e para o horário exibido.
        localBidIdRef.current -= 1

        const newBid: LiveArtBid = {
          id: localBidIdRef.current,
          bid_amount: bidAmount,
          tab_code: tabCode,
          participant_name: participantName,
          created_at: new Date().toISOString()
        }

        // A ordem do array nunca muda: o carrossel gira em ordem fixa de cadastro.
        setAllArtworks((current) =>
          current.map((artwork) =>
            artwork.id === artId
              ? {
                  ...artwork,
                  current_bid: bidAmount,
                  current_bid_tab_code: tabCode,
                  current_bid_participant_name: participantName,
                  recent_bids: [newBid, ...(artwork.recent_bids || [])].slice(0, ART_BIDS_LIMIT)
                }
              : artwork
          )
        )
      }

      if (eventType === 'auction_extended' || eventType === 'auction_closing') {
        setAuctionEndsAt((data.auction_ends_at as string) || null)
        setAuctionEnded(false)
      }

      if (eventType === 'auction_closed') {
        setAuctionEnded(true)
        // O worker acabou de gravar o sale_status de cada obra: só o snapshot traz isso.
        loadSnapshot()
      }

      if (eventType === 'auction_reopened') {
        setAuctionEndsAt(null)
        setAuctionEnded(false)
        loadSnapshot()
      }
    },
    [loadSnapshot]
  )

  const { isConnected } = useEventWebSocket({ eventId, onEvent: handleWsEvent })

  // Carga inicial e fallback por polling. Enquanto o WebSocket não estiver inscrito, a tela
  // busca o snapshot a cada 5s; ao (re)conectar, faz uma leitura de reconciliação e para o
  // polling — é a estratégia descrita em EVENT_TERMINAL_API.md. A reconexão em si é do hook.
  useEffect(() => {
    if (!eventId) return undefined

    loadSnapshot()

    if (isConnected) return undefined

    const interval = setInterval(loadSnapshot, POLLING_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [eventId, isConnected, loadSnapshot])

  // Entre o prazo vencer e o worker `auctions:close-due` decidir as obras existe uma janela
  // (até 1 min em produção, indefinida se o worker estiver parado) em que o leilão já conta
  // como encerrado mas nenhuma obra tem `sale_status` resolvido. Filtrar por "vendida" aí
  // esvaziaria a tela e anunciaria "nenhuma obra arrematada" antes da hora — por isso a
  // vitrine do resultado só entra quando o worker já passou, o que se reconhece por haver
  // ao menos uma obra decidida como sold ou unsold.
  const auctionFinalized =
    auctionEnded && allArtworks.some((artwork) => artwork.sale_status === 'sold' || artwork.sale_status === 'unsold')

  // Encerrado e finalizado, a tela vira uma vitrine do resultado: só o que foi arrematado.
  const artworks = useMemo(
    () => (auctionFinalized ? allArtworks.filter((artwork) => artwork.sale_status === 'sold') : allArtworks),
    [allArtworks, auctionFinalized]
  )

  // A lista encolhe no encerramento; sem isto o destaque poderia apontar para fora dela.
  useEffect(() => {
    setFeaturedIndex((index) => (artworks.length ? Math.min(index, artworks.length - 1) : 0))
  }, [artworks.length])

  // Rotação do carrossel: 7s por obra, reiniciados sempre que o destaque muda (inclusive
  // quando alguém usa as setas). Lance novo não mexe no destaque, por decisão do SDD.
  useEffect(() => {
    if (artworks.length < 2) return undefined

    const timer = setTimeout(() => {
      setFeaturedIndex((index) => (index + 1) % artworks.length)
    }, ROTATION_INTERVAL_MS)

    return () => clearTimeout(timer)
  }, [featuredIndex, artworks.length])

  const featured = artworks[featuredIndex] || null
  featuredArtIdRef.current = featured?.id ?? null

  const goToIndex = useCallback(
    (index: number) => {
      if (!artworks.length) return
      const total = artworks.length
      setFeaturedIndex(((index % total) + total) % total)
    },
    [artworks.length]
  )

  const goToNext = useCallback(() => goToIndex(featuredIndex + 1), [goToIndex, featuredIndex])
  const goToPrevious = useCallback(() => goToIndex(featuredIndex - 1), [goToIndex, featuredIndex])

  return {
    artworks,
    featured,
    featuredIndex,
    peopleCount,
    auctionEndsAt,
    auctionEnded,
    auctionFinalized,
    isConnected,
    isLoading,
    loadError,
    goToIndex,
    goToNext,
    goToPrevious
  }
}
